import { useListApplicantIncomeDetailsQuery } from "@lending/hooks/queries/ApplicantIncomeDetails/useListApplicantIncomeDetailsQuery";
import { useListApplicationApplicantDetailsQuery } from "@lending/hooks/queries/ApplicationApplicantDetails/useListApplicationApplicantDetailsQuery";
import { useFlowType } from "@lending/hooks/useFlowType";
import { useLendingApplicationID } from "@lending/hooks/useLendingApplicationID";

export interface PrefilledOlbSteps {
  hasPrefilledLivingArrangement: boolean;
  hasPrefilledIncome: boolean;
  hasPrefilledEmployment: boolean;
}

/**
 * Detects which OLB steps a sidebar-launched application already has data for, so the flow
 * can skip them (see olbStepConditions). Reads the current application's primary applicant
 * details and income — the same data the steps would collect.
 *
 * Only runs for the OLB flow with an application in the path. Every flag defaults to
 * `false` while data is loading or absent, so a step is only ever skipped on positive
 * confirmation that its data is present — never skipped on incomplete data.
 */
export function usePrefilledOlbSteps(): PrefilledOlbSteps {
  const flowType = useFlowType();
  const applicationUuid = useLendingApplicationID();
  const enabled = flowType === "olb" && Boolean(applicationUuid);

  const { data: applicantDetails } = useListApplicationApplicantDetailsQuery({
    applicationUuid,
    enabled,
  });
  const { data: incomeDetails } = useListApplicantIncomeDetailsQuery({ enabled });

  const primaryApplicant = applicantDetails?.results?.find(
    (applicant) => applicant.application_relationship === "primary"
  );
  const primaryIncome = incomeDetails?.results?.find((income) => income.is_primary);

  const hasPrefilledLivingArrangement = Boolean(primaryApplicant?.housing_status);
  const hasPrefilledIncome = Boolean(primaryIncome);
  // This flag gates the employment-details step, which collects the income amount, so a row
  // only counts as prefilled once it carries one. Without that check the row the add-income
  // step creates (amount 0) would look prefilled and skip the amount page. Retirement income
  // needs no employer or title; every other type needs both to be genuinely complete.
  const hasPrefilledEmployment =
    Boolean(primaryIncome?.amount) &&
    (primaryIncome?.income_type === "retirement" ||
      (Boolean(primaryIncome?.employer_name) && Boolean(primaryIncome?.job_title)));

  return { hasPrefilledLivingArrangement, hasPrefilledIncome, hasPrefilledEmployment };
}
